import {
  createApi,
  fetchBaseQuery,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/dist/query/react";

import * as devConfig from "../app/configs/devConfig";
import { getAuthHeader } from "../app/globalState/GlobalAuthState";
import {
  CustomerModel,
  CustomerModelView,
} from "../factories/customers/models/customerModel";
import { InvoiceTime } from "../features/time/InvoiceTime";
import { IContact, IUser, IWorkType } from "../types/types";

/** The browser's IANA time zone (e.g. "America/Vancouver"); "" when unknown. */
const browserTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
};

export enum FilterEnum {
  LastYear = "LastYear",
  YTD = "YTD",
  LastMonth = "LastMonth",
  ThisMonth = "ThisMonth",
  Today = "Today",
  Custom = "Custom",
}

export enum SortEnum {
  Customer = "Customer",
  InvoiceNo = "InvoiceNo",
  InvoiceDate = "InvoiceDate",
  DueDate = "DueDate",
  Total = "Total",
  Balance = "Balance",
  AssignedUser = "AssignedUser",
  PaymentDate = "PaymentDate",
  Amount = "Amount",
  PaymentMethod = "PaymentMethod",
  Notes = "Notes",
  Status = "Status",
  PaymentStatus = "PaymentStatus",
  InvoiceStatus = "InvoiceStatus",
  /** Invoice list Label column (QA batch 17; sorted on the server). */
  Label = "Label",
}

export type BillingCustomerType = "Business" | "Individual" | string;

export type BillingLinkedCustomer = {
  clientName: string;
  customerType: BillingCustomerType;
};

export interface Invoice {
  contactId: string;
  businessContactId: string;
  invoiceId: string;
  isIncluded: boolean;
  createdByUserId: string;
  assignedToUserId: string;
  status: string;
  taxCodeId: string;
  customerName: string;
  invoiceNumber: string;
  createdByUser: IUser;
  assignedToUser: IUser;
  invoiceNo: number;
  total: number;
  discount: number;
  linkedCustomerCounts: number;
  linkedCustomerNames: BillingLinkedCustomer[] | null;
  discountTypeId: string;
  paymentRecieved: number;
  tax: number;
  taxRateId: string;
  customerType: string;
  balance: number;
  description: null | string;
  invoiceDate: string;
  dueDate: string;
  isPayed: boolean;
  termId: string;
  term: { name: string };
  customerId: string;
  orgId: string;
  invoiceItem: InvoiceItem[];
  invoiceTerms: ITerm;
  customer: Customer;
  id: string;
  relayId: null | string;
  createdAt: string;
  sumOfBalance: number;
  sumOfTotal: number;
  invoiceCustomers: IInvoiceCustomers[];
  invoiceStatus: BillingStatusType | null;
  invoiceStatusId: string | null;
  invoiceClientsIds: string[];
  notes: string;
  imageURL: string | null;
  userColor: string;
  userFirstName: string;
  userLastName: string;
  userId: string;
  label: string;
  labelId: string;
  labelColor: string;
  totalRecords: number;
}
export interface IInvoiceView {
  contactId: string;
  businessContactId: string;
  invoiceId: string;
  isIncluded: boolean;
  createdByUserId: string;
  assignedToUserId: string;
  status: string;
  taxCodeId: string;
  customerName: string;
  invoiceNumber: string;
  createdByUser: IUser;
  assignedToUser: IUser;
  invoiceNo: number;
  total: number;
  discount: number;
  linkedCustomerCounts: number;
  discountTypeId: string;
  paymentRecieved: number;
  tax: number;
  taxRateId: string;
  customerType: string;
  balance: number;
  description: null | string;
  invoiceDate: string;
  dueDate: string;
  isPayed: boolean;
  termId: string;
  term: { name: string };
  customerId: string;
  orgId: string;
  invoiceItem: InvoiceItem[];
  invoiceTerms: ITerm;
  customer: CustomerView;
  id: string;
  relayId: null | string;
  createdAt: string;
  sumOfBalance: number;
  sumOfTotal: number;
  invoiceCustomers: IInvoiceCustomers[];
  invoiceStatus: BillingStatusType | null;
  invoiceStatusId: string | null;
  invoiceClientsIds: string[];
  notes: string;
  imageURL: string | null;
  userColor: string;
  userFirstName: string;
  userLastName: string;
  userId: string;
  label: string;
  labelId: string;
  labelColor: string;
}

export interface IInvoiceCustomers {
  id: string;
  customerId: string;
  invoiceId: string;
  customers: { name: string; businessContactId?: string; contactId?: string };
}

export interface IPaymentItem {
  id: string;
  relayId: string;
  createdAt: string;
  amountPaid: number;
  invoiceTotal: number;
  invoiceNo: number;
  invoiceDueDate: string;
  paymentId: string;
  invoiceId: string;
  invoiceBalance: number;
}

export interface IPayment {
  paymentClients: {
    name: string;
    id: string;
    contactId: string;
    businessContactId: string;
  }[];
  createdByUserId: string;
  createdByUser: IUser;
  amount: number;
  contactId: string;
  businessContactId: string;
  customerName: string;
  notes: null | string;
  paymentDate: string;
  customerId: string;
  method: string;
  orgId: string;
  paymentMethodName: string;
  paymentId: string;
  paymentMethodId: string;
  customer: Customer;
  paymentMethod: { paymentMethod: string; id: string };
  id: string;
  relayId: null | string;
  sumOfAmount?: number;
  createdAt: string;
  paymentStatus: BillingStatusType | null;
  paymentStatusId: string | null;
  paymentItem: IPaymentItem[];
  imageURL: string | null;
  customerCounts: number;
  linkedCustomerNames?: BillingLinkedCustomer[] | null;
  totalRecords: number;
}

interface Customer {
  name: string;
  customerTypeEnumId: string;
  contactId: string;
  businessContact: CustomerModel;
  contact: CustomerModel;
  businessContactId: null | string;
  locationId: string;
  id: string;
  relayId: null | string;
  createdAt: string;
  addresses: ICustomerAddress[];
}
interface CustomerView {
  name: string;
  customerTypeEnumId: string;
  contactId: string;
  businessContact: CustomerModelView;
  contact: CustomerModelView;
  businessContactId: null | string;
  locationId: string;
  id: string;
  relayId: null | string;
  createdAt: string;
  addresses: ICustomerAddress[];
}

interface ICustomerAddress {
  city: string;
  region: string;
  country: string | null;
  line1: string;
  line2: string | null;
  postalCode: string;
  customerId: string;
  addresTypeEnumId: string | null;
  id: string;
  relayId: string | null;
  createdAt: string;
}

export interface InvoiceItem {
  description: string;
  quantity: number;
  rate: number;
  amount: number;
  billingServiceId: string;
  billingService: { id: string; name: string };
  invoiceId: string;
  createdByUserId: null | string;
  id: string;
  taxCodeId: string;
  relayId: null | string;
  createdAt: string;
  invoiceType: "billedItem" | "invoiceItem";
  assignedUserId: string;
  workTypeId: string;
  workId: string | null;
  /** The linked work item (get-by-id includes it). */
  work?: { id: string; name?: string | null } | null;
  duration: number;
}

export interface IAddInvoice {
  isIncluded: boolean;
  notes: string;
  customerId: string;
  assignedToUserId: string | null;
  createdByUserId: string;
  invoiceDate: string;
  termId: string | null;
  dueDate: string;
  invoiceNo: number;
  tax: number;
  orgId: string;
  discount: number;
  discountTypeId: string;
  total: number;
  taxRateId: string;
  invoiceItem: IAddInvoiceItem[];
  invoiceCustomers: { customerId: string }[];
}
export interface IEditInvoice {
  isIncluded: boolean;
  id: string;
  customerId: string;
  notes: string;
  invoiceClientsIds: string[] | null;
  createdByUserId: string;
  invoiceDate: string;
  termId: string | null;
  dueDate: string;
  invoiceNo: number;
  tax: number;
  orgId: string;
  total: number;
  discount: number;
  balance: number;
  discountTypeId: string;
  invoiceStatusId: string | null;
  paymentRecieved: number;
  assignedToUserId: string | null;
  invoiceItem: IAddInvoiceItem[];
  invoiceCustomers: { customerId: string }[];
  /**
   * Edit window (QA C3, 2026-10-22): invoiceItem is the complete list, so the
   * server removes lines that are no longer there and frees their time.
   */
  replaceInvoiceItems?: boolean;
}

interface IAddInvoiceItem {
  description: string;
  quantity: number;
  rate: number;
  amount: number;
  billingServiceId: string;
  id?: string;
  invoiceId?: string;
  taxCodeId: string;
}

export interface ITerm {
  termName: string;
  id: string;
  relayId: null | string;
  createdAt: string;
}

export interface IAddPayment {
  paymentDate: string;
  amount: number;
  description?: string;
  balance: number;
  notes: string;
  orgId: string;
  paymentMethodId: string;
  customerId: string;
  userId: string;
  /** The person confirmed paying more than a balance (QA batch 17). */
  allowOverpayment?: boolean;
  paymentItem: { invoiceId: string; amountPaid: number }[];
}

export interface IUpdatePayment {
  paymentDate: string;
  amount: number;
  id: string;
  description?: string;
  balance: number;
  notes: string;
  paymentMethodId: string;
  paymentStatusId: string | null;
  customerId: string;
  /** The person confirmed paying more than a balance (QA batch 17). */
  allowOverpayment?: boolean;
  /** paymentItem is the whole split: invoices left out lose this payment (PAY-018). */
  replacePaymentItems?: boolean;
  paymentItem: { invoiceId: string; amountPaid: number; id?: string }[];
}

export interface IService {
  name: string;
  description: string;
  rate: number;
  orgId: string;
  createdAt: string;
  id: string;
}

export interface IAddService {
  name: string;
  description: string;
  rate: number;
  orgId: string;
}

export interface IUpdateService extends IAddService {
  id: string;
}

export interface IPaymentMethod {
  paymentMethod: string;
  id: string;
  relayId: string | null;
  createdAt: string;
}

export interface ITaxRate {
  isSyncedWithQBO: boolean;
  taxCodeName: string;
  taxCodeId: string;
  qboTaxCodeId: string;
  taxRates: { taxName: string; taxRate: number; id: string }[];
  id?: string;
  taxId?: string;
  createdAt?: string;
  orgId: string;
  isActive: boolean;
  /** The firm's default tax code for new invoices (one per firm). */
  isDefault?: boolean;
}
export interface IIvoiceStats {
  totalInvoices: number;
  totalOutStandings: number;
  totalPastDue: number;
  /** This year's overpaid invoices (count as $0 in the card, QA batch 17). */
  overpaidInvoices?: { id: string; invoiceNo: number; balance: number }[];
}

export type BillingLogo = {
  imageURI: string;
};

export type BillingStatusType = {
  id: string;
  name: string;
  color: string;
  statusName?: string;
  statusId?: string;
  statusColor?: string;
};

export type WorkBillingInvoice = {
  id: string;
  invoiceNo: number;
  invoiceDate: string | null;
  dueDate: string | null;
  status: string | null;
  statusColor: string | null;
  total: number;
  balance: number;
  isPaid: boolean;
  /** Sum of this invoice's lines linked to the work item. */
  workAmount: number;
  workLines: number;
};

export type WorkBilling = {
  invoices: WorkBillingInvoice[];
  billed: number;
  paid: number;
  /** Billable time on the work item not on an invoice yet. */
  unbilledSeconds: number;
  customerId: string | null;
};

export const billingAPI = createApi({
  reducerPath: "billingAPI",
  tagTypes: [
    "Invoices",
    "Payment",
    "PaymentMethods",
    "Services",
    "TaxRate",
    "Logo",
    "Content",
    "Invoice_Statuses",
    "Payment_Statuses",
  ],
  baseQuery: fetchBaseQuery({
    baseUrl: `${devConfig.getServerBaseUrlForEnv()}`,
    prepareHeaders: (headers) => {
      const authHeader = getAuthHeader();
      headers.set("Authorization", authHeader.Authorization);
      return headers;
    },
  }),
  endpoints: (build) => ({
    getInvoices: build.query<
      Invoice[],
      {
        orgId: string;
        filterByDate: string[];
        fromDate?: string | null;
        toDate?: string | null;
        sortOrder?: "desc" | "asc";
        sortBy?: SortEnum;
        filterByStatus: string[];
        filterByClient: string[];
        filterByAssignee: string[];
        filterByLabel: string[];
        pageNumber?: number;
        pageSize?: number;
      }
    >({
      query: (body) => ({
        url: "api/invoice/filter-invoice",
        body,
        method: "POST",
      }),
      providesTags: ["Invoices"],
    }),
    /** Every invoice of the firm with its lines (Reports > Revenue by service). */
    getAllInvoices: build.query<Invoice[], string>({
      query: (orgId) => ({
        url: "api/invoice/get-all/" + orgId,
      }),
      providesTags: ["Invoices"],
    }),
    getInvoiceById: build.query<IInvoiceView, string>({
      query: (invoiceId = "") => ({
        url: "api/invoice/get-by-id/" + invoiceId,
      }),
      providesTags: ["Invoices"],
    }),
    /** A work item's invoices (lines linked to it) and billing totals. Admin / "View Billings" only (403 otherwise). */
    getWorkBilling: build.query<WorkBilling, string>({
      query: (workId) => ({
        url: "api/invoice/by-work/" + workId,
      }),
      providesTags: ["Invoices", "Payment"],
    }),
    /** Roadmap 1789: email the invoice PDF from ProCharted's no-reply sender (Admin / "View Billings"). */
    sendInvoiceNoReply: build.mutation<
      { sent: number; to: string[]; from: string },
      {
        invoiceId: string;
        to: string[];
        subject: string;
        message: string;
        pdfBase64: string;
        fileName: string;
      }
    >({
      query: ({ invoiceId, ...body }) => ({
        url: `api/invoice/${invoiceId}/send-no-reply`,
        method: "POST",
        body,
      }),
    }),
    getPaymentsRecieved: build.query<number, string>({
      query: (invoiceId = "") => ({
        url: "api/invoice/payments-recieved/" + invoiceId,
      }),
      providesTags: ["Invoices", "Payment"],
    }),
    getInvoicesByCustomer: build.query<Invoice[], string>({
      query: (customerId) => ({
        url: "api/invoice/get-by-customer/" + customerId,
      }),
      providesTags: ["Invoices"],
    }),
    getInvoiceTerms: build.query<ITerm[], { orgId: string }>({
      query: ({ orgId }) => ({
        url: "api/invoice/get-invoice-terms/",
        params: { orgId },
      }),
    }),
    getInvoiceStats: build.query<IIvoiceStats, string>({
      // Past due = due DAY before today in the browser's time zone (QA DATA-003).
      query: (orgId) => ({
        url: "api/invoice/get-invoice-amounts/" + orgId,
        params: { timeZone: browserTimeZone() },
      }),
      providesTags: ["Invoices"],
    }),
    getInvoiceNo: build.query<number, string>({
      query: (orgId) => ({
        url: "api/invoice/get-invoice-no/" + orgId,
      }),
    }),
    getBilledWorks: build.query<InvoiceTime[], { invoiceId: string }>({
      query: ({ invoiceId }) => ({
        url: `/api/invoice-item/get-billed-item/${invoiceId}`,
      }),
    }),
    getWorkTypes: build.query<IWorkType[], string>({
      query: (orgId) => ({
        url: "api/services/get-all",
        params: {
          orgId,
        },
      }),
    }),
    getServices: build.query<IService[], string>({
      query: (orgId) => ({
        url: "api/invoice-services/get-all/" + orgId,
      }),
      providesTags: ["Services"],
    }),
    addService: build.mutation<unknown, IAddService>({
      query: (body) => ({
        url: "api/invoice-services/add",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Services"],
    }),
    updateService: build.mutation<unknown, IUpdateService>({
      query: (body) => ({
        url: "api/invoice-services/update",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Services"],
    }),
    deleteService: build.mutation<unknown, string>({
      query: (id) => ({
        url: "api/invoice-services",
        method: "DELETE",
        body: { id },
      }),
      invalidatesTags: ["Services"],
    }),
    addInvoice: build.mutation<Invoice, IAddInvoice>({
      query: (body) => ({
        url: "api/invoice/add-invoice",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Invoices"],
    }),
    addInvoiceItem: build.mutation<IAddInvoiceItem, IAddInvoiceItem>({
      query: (body) => ({
        url: "api/invoice-item/add",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Invoices"],
    }),
    deleteInvoiceItem: build.mutation<unknown, string>({
      query: (id) => ({
        url: "api/invoice-item",
        method: "DELETE",
        body: { id },
      }),
      invalidatesTags: ["Invoices"],
    }),
    updateInvoice: build.mutation<IEditInvoice | any, IEditInvoice | any>({
      query: (body) => ({
        url: "api/invoice/update-invoice",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Invoices"],
    }),
    // QA batch 12: change only the label (update-invoice with a list row zeroed the number).
    setInvoiceLabel: build.mutation<
      unknown,
      { id: string; labelId: string | null }
    >({
      query: ({ id, labelId }) => ({
        url: `api/invoice/${id}/label`,
        method: "PUT",
        body: { labelId: labelId || null },
      }),
      invalidatesTags: ["Invoices"],
    }),
    updateBilledStatus: build.mutation<unknown, string[]>({
      query: (body) => ({
        url: "api/time/update-billed-status",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Invoices"],
    }),
    deleteInvoice: build.mutation({
      query: (body: Invoice | IInvoiceView) => ({
        url: "api/invoice",
        method: "DELETE",
        body,
      }),
      invalidatesTags: ["Invoices"],
    }),
    deleteBilled: build.mutation({
      query: (body: Invoice) => ({
        url: "api/invoice-item/delete-billed",
        method: "DELETE",
        body,
      }),
      invalidatesTags: ["Invoices"],
    }),
    getPayments: build.query<
      IPayment[],
      {
        orgId: string;
        filterByDate: string[];
        fromDate?: string | null;
        toDate?: string | null;
        sortBy?: SortEnum;
        sortOrder?: "desc" | "asc";
        filterByStatus: string[];
        filterByClient: string[];
        filterByAssignee: string[];
        filterByLabel: string[];
        pageNumber?: number;
        pageSize?: number;
      }
    >({
      query: (body) => ({
        url: "api/payment/filter-payment",
        method: "POST",
        body,
      }),
      providesTags: () => ["Payment"],
    }),
    getPaymentById: build.query<IPayment, string>({
      query: (id) => ({
        url: "api/payment/get-by-id/" + id,
        method: "GET",
      }),
      providesTags: () => ["Payment"],
    }),
    getPaymentAmounts: build.query<{ totalPayments: number }, string>({
      query: (orgId) => ({
        url: "api/payment/get-payment-amounts/" + orgId,
        method: "GET",
      }),
      providesTags: () => ["Payment"],
    }),
    getPaymentByInvoiceId: build.query<IPayment[], string>({
      query: (invoiceId) => ({
        url: "api/payment/payments-by-invoice/" + invoiceId,
        method: "GET",
      }),
      providesTags: () => ["Payment"],
    }),
    getPaymentMethods: build.query<IPaymentMethod[], string>({
      query: (orgId) => ({
        url: "api/payment-methods/get-all/" + orgId,
      }),
      providesTags: ["PaymentMethods"],
    }),
    addPaymentMethod: build.mutation<
      void,
      { paymentMethod: string; orgId: string }
    >({
      query: (body) => ({
        url: "api/payment-methods/add",
        method: "POST",
        body,
      }),
      invalidatesTags: ["PaymentMethods"],
    }),
    updatePaymentMethod: build.mutation<
      void,
      { paymentMethod: string; id: string }
    >({
      query: (body) => ({
        url: "api/payment-methods/update",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["PaymentMethods"],
      transformErrorResponse: (res: FetchBaseQueryError): string => {
        const error = res?.data as string;
        return error || "";
      },
    }),
    deletePaymentMethod: build.mutation<void, string>({
      query: (id) => ({
        url: "api/payment-methods",
        method: "DELETE",
        body: { id },
      }),
      invalidatesTags: ["PaymentMethods"],
    }),
    addPayment: build.mutation({
      query: (body: IAddPayment) => ({
        url: "api/payment/add-payment",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Payment", "Invoices"],
      transformErrorResponse: (res: FetchBaseQueryError): string => {
        const error = res?.data as string;
        return error || "";
      },
    }),
    updatePayment: build.mutation<unknown, IUpdatePayment>({
      query: (body) => ({
        url: "api/payment/update-payment",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Payment", "Invoices"],
    }),
    setPaymentLabel: build.mutation<
      unknown,
      { id: string; labelId: string | null }
    >({
      query: ({ id, labelId }) => ({
        url: `api/payment/${id}/label`,
        method: "PUT",
        body: { labelId: labelId || null },
      }),
      invalidatesTags: ["Payment", "Invoices"],
    }),
    deletePayment: build.mutation({
      query: (body: IPayment) => ({
        url: "api/payment",
        method: "DELETE",
        body,
      }),
      invalidatesTags: ["Payment", "Invoices"],
    }),
    getTaxRate: build.query<ITaxRate[], string>({
      query: (orgId) => ({
        url: "api/tax-code/get-all/" + orgId,
      }),
      providesTags: () => ["TaxRate"],
    }),
    getActiveTaxRate: build.query<ITaxRate[], string>({
      query: (orgId) => ({
        url: "api/tax-code/get-active/" + orgId,
      }),
      providesTags: () => ["TaxRate"],
    }),
    addTaxRate: build.mutation<
      void,
      {
        taxCodeName: string;
        isActive: boolean;
        taxRates: { taxName: string; taxRate: number }[];
        orgId: string;
      }
    >({
      query: (body) => ({
        url: "api/tax-code/add",
        method: "POST",
        body,
      }),
      invalidatesTags: ["TaxRate"],
    }),
    /**
     * Edit a tax code (owner 2026-10-22: only in Settings > Billing > Tax Rate). Rates of a code
     * already used on invoices are never changed: the server makes a new code and switches the old
     * one off (newVersion), so saved invoices keep their amounts.
     */
    updateTaxRate: build.mutation<
      { taxCode?: ITaxRate; newVersion: boolean; previousId?: string | null },
      {
        id: string;
        orgId: string;
        taxCodeName: string;
        isActive: boolean;
        taxRates: { taxName: string; taxRate: number }[];
      }
    >({
      query: (body) => ({
        url: "api/tax-code/update",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["TaxRate"],
    }),
    /** Makes this tax code the firm's default for new invoices (Roadmap 1791). */
    setDefaultTaxCode: build.mutation<
      void,
      { orgId: string; taxCodeId: string }
    >({
      query: (params) => ({
        url: "api/tax-code/update-default",
        method: "PUT",
        params,
      }),
      invalidatesTags: ["TaxRate"],
    }),
    deleteTaxCode: build.mutation<
      void,
      { taxCodeId: string; isActive: boolean; orgId: string }
    >({
      query: (params) => ({
        url: "api/tax-code/in-active-tax-code",
        method: "DELETE",
        params,
      }),
      invalidatesTags: ["TaxRate"],
    }),
    deleteTaxRate: build.mutation<
      void,
      { taxRateId: string; isActive: boolean; orgId: string }
    >({
      query: (params) => ({
        url: "api/tax-code/in-active-tax-rate",
        method: "DELETE",
        params,
      }),
      invalidatesTags: ["TaxRate"],
    }),
    getContactById: build.query<IContact, { id: string; orgId: string }>({
      query: ({ id, orgId }) => ({
        url: `api/contacts/get/${id}?orgId=${orgId}`,
        method: "GET",
      }),
      providesTags: ["Payment"],
    }),
    getBillingLogo: build.query<BillingLogo, string>({
      query: (id) => ({
        url: `api/firm-logo/get-by-firm/${id}`,
        method: "GET",
      }),
      providesTags: ["Logo"],
    }),
    addBillingLogo: build.mutation<void, unknown>({
      query: (body) => ({
        url: "api/firm-logo/add-logo",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Logo"],
    }),
    updateBillingLogo: build.mutation<void, unknown>({
      query: (body) => ({
        url: "api/firm-logo/update-logo",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Logo"],
    }),
    deleteBillingLogo: build.mutation<void, string>({
      query: (orgId) => ({
        url: `api/firm-logo?orgId=${orgId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Logo"],
    }),
    getBillingContent: build.query<
      { notes: string; footer: string; orgId: string; id: string },
      string
    >({
      query: (orgId) => ({
        url: `api/invoice-content/get-by-org/${orgId}`,
        method: "GET",
      }),
      providesTags: ["Content"],
    }),
    updateBillingContent: build.mutation<
      void,
      { notes: string; footer: string; orgId: string; id: string }
    >({
      query: (body) => ({
        url: "api/invoice-content/update-content",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Content"],
    }),
    addBillingContent: build.mutation<
      void,
      { notes: string; footer: string; orgId: string }
    >({
      query: (body) => ({
        url: "api/invoice-content/add-content",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Content"],
    }),
    deleteBillingContent: build.mutation<void, string>({
      query: (orgId) => ({
        url: `api/invoice-content/delete-content?orgId=${orgId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Content"],
    }),
    getInvoiceStatuses: build.query<BillingStatusType[], string>({
      query: (id) => ({
        url: `api/invoice-status/get-by-orgId/${id}`,
        method: "GET",
      }),
      providesTags: ["Invoice_Statuses"],
    }),
    getAllInvoiceStatuses: build.query<BillingStatusType[], string>({
      query: (id) => ({
        url: `api/invoice-status/get-all/${id}`,
        method: "GET",
      }),
      providesTags: ["Invoice_Statuses"],
    }),
    addInvoiceStatus: build.mutation<void, unknown>({
      query: (body) => ({
        url: "api/invoice-status/add-status",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Invoice_Statuses"],
    }),
    updateInvoiceStatus: build.mutation<void, unknown>({
      query: (body) => ({
        url: "api/invoice-status/update-status",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Invoice_Statuses"],
    }),
    reorderInvoiceStatus: build.mutation<void, string[]>({
      query: (body) => ({
        url: "api/invoice-status/re-order-status",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Invoice_Statuses"],
    }),
    deleteInvoiceStatus: build.mutation<void, string>({
      query: (id) => ({
        url: `api/invoice-status/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Invoice_Statuses"],
    }),
    getPaymentStatuses: build.query<BillingStatusType[], string>({
      query: (id) => ({
        url: `api/payment-status/get-by-orgId/${id}`,
        method: "GET",
      }),
      providesTags: ["Payment_Statuses"],
    }),
    getAllPaymentStatuses: build.query<BillingStatusType[], string>({
      query: (id) => ({
        url: `api/payment-status/get-all/${id}`,
        method: "GET",
      }),
      providesTags: ["Payment_Statuses"],
    }),
    addPaymentStatus: build.mutation<void, unknown>({
      query: (body) => ({
        url: "api/payment-status/add-status",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Payment_Statuses"],
    }),
    reorderPaymentStatus: build.mutation<void, string[]>({
      query: (body) => ({
        url: "api/payment-status/re-order-status",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Payment_Statuses"],
    }),
    updatePaymentStatus: build.mutation<void, unknown>({
      query: (body) => ({
        url: "api/payment-status/update-status",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Payment_Statuses"],
    }),
    deletePaymentStatus: build.mutation<void, string>({
      query: (id) => ({
        url: `api/payment-status/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Payment_Statuses"],
    }),
  }),
});
export const {
  //Invoices
  useGetWorkBillingQuery,
  useSendInvoiceNoReplyMutation,
  useGetInvoicesQuery,
  useLazyGetInvoicesQuery,
  useGetInvoiceNoQuery,
  useAddInvoiceMutation,
  useDeleteInvoiceMutation,
  useGetInvoicesByCustomerQuery,
  useLazyGetInvoicesByCustomerQuery,
  useGetInvoiceTermsQuery,
  useGetInvoiceByIdQuery,
  useUpdateInvoiceMutation,
  useSetInvoiceLabelMutation,
  useAddInvoiceItemMutation,
  useDeleteInvoiceItemMutation,
  useGetInvoiceStatsQuery,
  useGetPaymentsRecievedQuery,
  useGetBilledWorksQuery,
  useUpdateBilledStatusMutation,
  useDeleteBilledMutation,

  //Payment
  useGetPaymentsQuery,
  useGetPaymentByInvoiceIdQuery,
  useGetPaymentByIdQuery,
  useGetPaymentAmountsQuery,
  useAddPaymentMutation,
  useUpdatePaymentMutation,
  useSetPaymentLabelMutation,
  useDeletePaymentMutation,

  //Payment methods
  useGetPaymentMethodsQuery,
  useLazyGetPaymentMethodsQuery,
  useAddPaymentMethodMutation,
  useUpdatePaymentMethodMutation,
  useDeletePaymentMethodMutation,

  //Services
  useGetServicesQuery,
  useAddServiceMutation,
  useUpdateServiceMutation,
  useDeleteServiceMutation,

  //Tax rate
  useGetTaxRateQuery,
  useGetActiveTaxRateQuery,
  useAddTaxRateMutation,
  useUpdateTaxRateMutation,
  useDeleteTaxCodeMutation,
  useSetDefaultTaxCodeMutation,
  useDeleteTaxRateMutation,
  // useGetContactByIdQuery,

  //Logo
  useGetBillingLogoQuery,
  useAddBillingLogoMutation,
  useUpdateBillingLogoMutation,
  useDeleteBillingLogoMutation,

  //Content
  useGetBillingContentQuery,
  useAddBillingContentMutation,
  useUpdateBillingContentMutation,
  useDeleteBillingContentMutation,

  //Invoice statuses
  useGetInvoiceStatusesQuery,
  useGetAllInvoiceStatusesQuery,
  useAddInvoiceStatusMutation,
  useUpdateInvoiceStatusMutation,
  useReorderInvoiceStatusMutation,
  useDeleteInvoiceStatusMutation,

  //Payment statuses
  useGetPaymentStatusesQuery,
  useGetAllPaymentStatusesQuery,
  useAddPaymentStatusMutation,
  useReorderPaymentStatusMutation,
  useUpdatePaymentStatusMutation,
  useDeletePaymentStatusMutation,
} = billingAPI;
